/**
 * Ho-SYS API Client
 * Client API directe per a FastAPI backend
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// In-memory token fallback (for when httpOnly cookies don't work cross-domain)
let _memToken = null;

export function setMemToken(token) { _memToken = token; }
export function getMemToken() { return _memToken; }

// ─── Core HTTP helper ─────────────────────────────────────────────────────────
async function apiRequest(endpoint, options = {}) {
  const url = `${API_URL}${endpoint}`;
  const config = {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  // Add Bearer token as fallback if cookie auth might not work
  if (_memToken) {
    config.headers['Authorization'] = `Bearer ${_memToken}`;
  }

  const response = await fetch(url, config);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Request failed');
  }

  return response.json();
}

// ─── Entity endpoint mapping ──────────────────────────────────────────────────
const ENTITY_ENDPOINTS = {
  User: 'users',
  Venue: 'venues',
  VenueMember: 'venue_members',
  Subscription: 'subscriptions',
  TimeRecord: 'time_records',
  TemperatureRecord: 'temperature_records',
  CleaningRecord: 'cleaning_records',
  ChlorineRecord: 'chlorine_records',
  Incident: 'incidents',
  Alert: 'alerts',
  AlertSettings: 'alert_settings',
  Document: 'documents',
  RecipeSheet: 'recipe_sheets',
  Ingredient: 'ingredients',
  Verification: 'verifications',
  EquipmentConfig: 'equipment_configs',
  OvertimeRecord: 'overtime_records',
  WorkerContract: 'worker_contracts',
  Invitation: 'invitations',
  APPCCPlan: 'appcc_plans',
  APPCCVerification: 'appcc_verifications',
  HazardAnalysis: 'hazard_analyses',
  CriticalControlPoint: 'critical_control_points',
  CorrectionAction: 'correction_actions',
  CleaningArea: 'cleaning_areas',
  CleaningTask: 'cleaning_tasks',
  MonthlySchedule: 'monthly_schedules',
  MonthlyPlannedHours: 'monthly_planned_hours',
  Template: 'templates',
  BusinessTemplate: 'business_templates',
  GeneratedDocument: 'generated_documents',
  InvoiceScan: 'invoice_scans',
  Invoice: 'invoices',
  Payment: 'payments',
  GDPRRecord: 'gdpr_records',
  DataProcessingActivity: 'data_processing_activities',
  DataConsent: 'data_consents',
  DataIncident: 'data_incidents',
  PRLRecord: 'prl_records',
  SafetyDocument: 'safety_documents',
  SafetyTraining: 'safety_trainings',
  WorkIncident: 'work_incidents',
  EquipmentMaintenance: 'equipment_maintenances',
  HarassmentRecord: 'harassment_records',
  HarassmentCase: 'harassment_cases',
  HarassmentTraining: 'harassment_trainings',
  RegulatoryCheck: 'regulatory_checks',
  ComplianceItem: 'compliance_items',
  TechnicalCompliance: 'technical_compliance',
  TechnicalMaintenance: 'technical_maintenances',
  ElectricalCompliance: 'electrical_compliance',
  ElectricalInstallation: 'electrical_installations',
  ElectricalInspection: 'electrical_inspections',
  ElectricalDeficiency: 'electrical_deficiencies',
  ElectricalDocument: 'electrical_documents',
  FireProtection: 'fire_protections',
  GasInstallation: 'gas_installations',
  KitchenEquipment: 'kitchen_equipments',
  NFCTag: 'nfc_tags',
  AuditLog: 'audit_logs',
  TimeAuditLog: 'time_audit_logs',
  StockMovement: 'stock_movements',
  ProductTechnicalSheet: 'product_technical_sheets',
  CleaningProduct: 'cleaning_products',
};

// ─── Entity CRUD factory ──────────────────────────────────────────────────────
const ENTITY_ID_OVERRIDES = {
  VenueMember: 'member_id',
  User: 'user_id',
};

function createEntity(entityName) {
  const endpoint = ENTITY_ENDPOINTS[entityName] || entityName.toLowerCase() + 's';
  const idFieldOverride = ENTITY_ID_OVERRIDES[entityName];

  return {
    async filter(filters = {}, sortBy = null) {
      try {
        if (entityName === 'Subscription' && filters.venue_id) {
          const response = await apiRequest(`/api/subscriptions/${filters.venue_id}`);
          if (response) {
            return [{ ...response, id: response.subscription_id || response.id }];
          }
          return [];
        }

        let url = `/api/data/${endpoint}`;
        const params = new URLSearchParams();

        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value);
          }
        });

        if (sortBy) params.append('sort', sortBy);
        if (params.toString()) url += `?${params.toString()}`;

        const items = await apiRequest(url);

        if (!Array.isArray(items)) return [];

        return items.map(item => ({
          ...item,
          id: idFieldOverride ? item[idFieldOverride] : (item.id || item[`${endpoint.slice(0, -1)}_id`] || item[`${entityName.toLowerCase()}_id`]),
        }));
      } catch (error) {
        console.error(`[API] ${entityName}.filter error:`, error);
        return [];
      }
    },

    async get(id) {
      try {
        const items = await this.filter({ id });
        return items[0] || null;
      } catch (error) {
        console.error(`[API] ${entityName}.get error:`, error);
        return null;
      }
    },

    async create(data) {
      return apiRequest(`/api/data/${endpoint}`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async update(id, data) {
      return apiRequest(`/api/data/${endpoint}/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    async delete(id) {
      return apiRequest(`/api/data/${endpoint}/${id}`, {
        method: 'DELETE',
      });
    },

    async list(filters = {}) {
      return this.filter(filters);
    },

    subscribe(callback) {
      let active = true;
      const poll = async () => {
        if (!active) return;
        try {
          const items = await this.filter({});
          if (active && callback) callback(items);
        } catch (e) {
          // silent
        }
      };
      poll();
      return () => { active = false; };
    },
  };
}

// ─── Build entities map ───────────────────────────────────────────────────────
const entities = {};
Object.keys(ENTITY_ENDPOINTS).forEach(name => {
  entities[name] = createEntity(name);
});

// ─── Generic collection helpers (direct collection-name access) ───────────────
entities.getAll = async (collection, venueId, sortBy) => {
  let url = `/api/data/${collection}`;
  const params = new URLSearchParams();
  if (venueId) params.append('venue_id', venueId);
  if (sortBy) params.append('sort', sortBy);
  if (params.toString()) url += `?${params.toString()}`;
  const items = await apiRequest(url);
  return Array.isArray(items) ? items.map(item => ({
    ...item,
    id: item.id || item[`${collection.replace(/s$/, '')}_id`],
  })) : [];
};

entities.create = async (collection, data) => {
  return apiRequest(`/api/data/${collection}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

entities.update = async (collection, id, data) => {
  return apiRequest(`/api/data/${collection}/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
};

entities.deleteItem = async (collection, id) => {
  return apiRequest(`/api/data/${collection}/${id}`, {
    method: 'DELETE',
  });
};

// ─── Auth ─────────────────────────────────────────────────────────────────────
const auth = {
  async me() {
    return apiRequest('/api/auth/me');
  },
  async login(email, password) {
    const response = await apiRequest('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    // Store token in memory as fallback for cross-domain cookie issues
    if (response.token) setMemToken(response.token);
    return response.user;
  },
  async logout() {
    await apiRequest('/api/auth/logout', { method: 'POST' }).catch(() => {});
    setMemToken(null);
    // Clear any sensitive data cached in sessionStorage so it doesn't leak
    // across user sessions on the same device.
    try {
      sessionStorage.clear();
    } catch {
      // ignore — sessionStorage may not be available
    }
    // Force navigation to login to ensure no in-memory React state lingers.
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  },
  async register(data) {
    const response = await apiRequest('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (response.token) setMemToken(response.token);
    return response;
  },
};

// ─── Users ────────────────────────────────────────────────────────────────────
const users = {
  async inviteUser(email, role) {
    return apiRequest('/api/users/invite', {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    });
  },
};

// ─── Server functions ─────────────────────────────────────────────────────────
const functions = {
  async invoke(functionName, params = {}) {
    if (functionName === 'uploadFile') {
      const res = await apiRequest('/api/upload/base64', {
        method: 'POST',
        body: JSON.stringify(params),
      });
      return { data: res };
    }
    return apiRequest('/api/functions/invoke', {
      method: 'POST',
      body: JSON.stringify({ function_name: functionName, params }),
    });
  },
};

// ─── Integrations (AI, file upload) ───────────────────────────────────────────
const integrations = {
  Core: {
    async InvokeLLM({ prompt, context, history }) {
      return apiRequest('/api/ai/chat', {
        method: 'POST',
        body: JSON.stringify({
          message: prompt,
          context: context || '',
          history: history || [],
        }),
      });
    },
    async UploadFile({ file }) {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `Upload failed: ${response.status}`);
      }
      return response.json();
    },
  },
};

// ─── APPCC Auto-Plan ──────────────────────────────────────────────────────────
const appcc = {
  async getAutoPlan(venueId) {
    return apiRequest(`/api/appcc/auto-plan/${venueId}`);
  },
  async getCompliance(venueId) {
    return apiRequest(`/api/appcc/compliance/${venueId}`);
  },
  async downloadPdf(venueId) {
    const res = await fetch(`${API_URL}/api/appcc/pdf/${venueId}`, {
      credentials: 'include',
    });
    if (!res.ok) throw new Error("Error generant PDF");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `APPCC_${venueId}_${new Date().toISOString().slice(0,10)}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
  async saveSnapshot(venueId) {
    return apiRequest(`/api/appcc/snapshot/${venueId}`, { method: "POST" });
  },
  async getHistory(venueId) {
    return apiRequest(`/api/appcc/history/${venueId}`);
  },
  async getSnapshot(snapshotId) {
    return apiRequest(`/api/appcc/snapshot/${snapshotId}`);
  },
  async downloadSnapshotPdf(snapshotId, filename) {
    const res = await fetch(`${API_URL}/api/appcc/snapshot/${snapshotId}/pdf`, {
      credentials: 'include',
    });
    if (!res.ok) throw new Error("Error generant PDF");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || `APPCC_snapshot.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
  async getAiAnalysis(venueId) {
    return apiRequest(`/api/appcc/ai-analysis/${venueId}`);
  },
};

// ─── Recipe PDF ───────────────────────────────────────────────────────────────
const recipePdf = {
  async download(recipeId, recipeName) {
    const res = await fetch(`${API_URL}/api/recipe/pdf/${recipeId}`, {
      credentials: 'include',
    });
    if (!res.ok) throw new Error("Error generant PDF");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Escandall_${(recipeName || "fitxa").replace(/\s/g, "_")}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
};

// ─── Risk Dashboard ───────────────────────────────────────────────────────────
const risk = {
  async getDashboard(venueId) {
    return apiRequest(`/api/risk/dashboard/${venueId}`);
  },
};

// ─── Exports ──────────────────────────────────────────────────────────────────
export const hosys = { entities, auth, users, functions, integrations, appcc, risk, recipePdf };
export default hosys;
